import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { UserSessionService } from '../../services/user-session.service';
import { ControlhelpService } from '../../services/controlhelp.service';
import { UserService } from '../../services/user.service';
import { OrgStructureService } from '../../services/org-structure.service';
import { API_BASE_URL } from '../../services/api-base';
import { toLocalISODate } from '../../utils/date-utils';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDatepicker } from '@angular/material/datepicker';
import { ChartConfiguration, ChartData } from 'chart.js';

type ControlHelpStatus =
  | 'OPEN'
  | 'IN_PROGRESS'
  | 'WAITING_USER'
  | 'WAITING_THIRD_PARTY'
  | 'RESOLVED'
  | 'CLOSED'
  | 'CANCELED';

type ControlHelpPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';

type RelatedModule = 'CONTROLTASK' | 'CONTROLDOCS' | 'CONTROLSIGN' | 'CONTROLCLI' | 'OTHER';

interface ControlHelpTicket {
  id: number;
  tenant_id?: number | null;
  ticket_code: string;
  title: string;
  description: string;
  status: ControlHelpStatus;
  priority: ControlHelpPriority;
  category?: string;
  subcategory?: string;
  department_id?: number | null;
  department_name?: string | null;
  created_by?: number | null;
  created_by_name?: string | null;
  assigned_to?: number | null;
  assigned_to_name?: string | null;
  related_module: RelatedModule;
  opened_at: string;
  first_in_progress_at?: string | null;
  resolved_at?: string | null;
  last_action_at?: string | null;
  response_at?: string | null;
  closed_at?: string | null;
  due_at?: string | null;
}

interface ControlHelpComment {
  id: number;
  user_id?: number;
  user_name?: string;
  comment: string;
  is_internal: boolean;
  created_at: string;
}

interface ControlHelpAttachment {
  id: number;
  comment_id?: number | null;
  file_name: string;
  file_path?: string;
  file_type?: string;
  file_size?: number;
  uploaded_by?: number;
  uploaded_by_name?: string;
  uploaded_at: string;
}

interface ControlHelpHistory {
  id: number;
  action_type: string;
  old_value?: string | null;
  new_value?: string | null;
  description?: string | null;
  user_id?: number | null;
  user_name?: string | null;
  created_at: string;
}

interface ControlHelpWatcher {
  id?: number;
  user_id: number;
  user_name?: string | null;
  created_at?: string;
}

interface ControlHelpDetailDraft {
  status: ControlHelpStatus;
  priority: ControlHelpPriority;
  assigned_to: string;
  due_at: string | null;
}

interface ControlHelpCategory {
  id: number;
  department_id?: number | null;
  name: string;
  subcategory?: string | null;
  description?: string;
  active: boolean;
}

interface ControlHelpSla {
  id: number;
  department_id?: number | null;
  priority: ControlHelpPriority;
  response_time_minutes: number;
  resolution_time_minutes: number;
}

@Component({
  selector: 'app-controlhelp',
  templateUrl: './controlhelp.component.html',
  styleUrls: ['./controlhelp.component.scss']
})
export class ControlhelpComponent implements OnInit {
  selectedTab: 'dashboard' | 'tickets' | 'categories' | 'sla' = 'tickets';
  ticketView: 'mine' | 'queue' = 'queue';
  tenantId: number | null = null;
  userId: number | null = null;
  userDepartmentId: number | null = null;
  userSectorId: number | null = null;
  controlhelpDepartmentIds: number[] = [];
  controlhelpEnabledSectorIds: number[] | null = null;
  controlhelpDestinationSectorIds: number[] | null = null;
  isMasterUser = false;
  isAdminUser = false;
  isAdminOrMaster = false;
  canManageControlhelp = false;
  controlhelpSectorEnabled = true;

  pageFeedback: { type: 'error' | 'success' | 'info'; message: string } | null = null;
  ticketFormFeedback = '';
  ticketFormFeedbackType: 'error' | 'success' | 'info' = 'info';
  detailFeedback = '';
  detailFeedbackType: 'error' | 'success' | 'info' = 'info';

  searchTerm = '';
  statusFilter: ControlHelpStatus | 'ALL' = 'ALL';
  priorityFilter: ControlHelpPriority | 'ALL' = 'ALL';
  departmentFilter: number | 'ALL' = 'ALL';
  sortOrder: 'oldest' | 'newest' = 'oldest';
  ticketPage = 1;
  readonly ticketPageSize = 10;
  dashboardPeriodMonths = 12;

  dashboardLineChartOptions: ChartConfiguration<'line'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    animation: { duration: 420 },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#15385f',
        titleColor: '#ffffff',
        bodyColor: '#e9f1ff',
        displayColors: true,
        cornerRadius: 10,
        padding: 10
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          color: '#7f92aa',
          font: { size: 12, weight: 'bold' },
          maxRotation: 0,
          minRotation: 0,
          autoSkip: true,
          maxTicksLimit: 12
        }
      },
      y: {
        beginAtZero: true,
        grid: { color: '#e7eef8' },
        ticks: {
          color: '#7f92aa',
          font: { size: 11, weight: 600 },
          precision: 0
        }
      }
    }
  };

  dashboardDonutChartOptions: ChartConfiguration<'doughnut'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '68%',
    events: ['click', 'touchstart'],
    animation: false,
    transitions: {
      active: {
        animation: {
          duration: 0
        }
      }
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#15385f',
        titleColor: '#ffffff',
        bodyColor: '#e9f1ff',
        displayColors: true,
        cornerRadius: 10,
        padding: 10
      }
    }
  };
  dashboardLineChartData: ChartData<'line'> = {
    labels: [],
    datasets: []
  };
  dashboardPriorityDonutData: ChartData<'doughnut'> = {
    labels: [],
    datasets: [
      {
        data: [1],
        backgroundColor: ['#e7eef8'],
        borderWidth: 0,
        hoverOffset: 6
      }
    ]
  };
  categorySearch = '';
  subcategorySearch = '';
  slaCategorySearch = '';
  slaSubcategorySearch = '';

  showNewTicketModal = false;
  showTicketDetail = false;
  ticketDetailTab: 'summary' | 'comments' | 'history' = 'summary';
  detailPanelOpen = {
    summary: false,
    statusPriority: false,
    assigneeDue: false,
    watchers: false
  };
  showNewCategoryModal = false;
  showEditCategoryModal = false;
  showNewSlaModal = false;
  showEditSlaModal = false;
  showGuideModal = false;
  newTicketStep = 1;
  guideSteps = [
    {
      title: 'Cadastre categorias',
      description: 'Organize os chamados por categoria e subcategoria.'
    },
    {
      title: 'Defina SLAs',
      description: 'Configure tempos de resposta e resolução por prioridade.'
    },
    {
      title: 'Abra o primeiro chamado',
      description: 'Teste o fluxo de tickets e acompanhe a fila.'
    }
  ];

  activeTicket: ControlHelpTicket | null = null;
  ticketComments: ControlHelpComment[] = [];
  ticketAttachments: ControlHelpAttachment[] = [];
  ticketWatchers: ControlHelpWatcher[] = [];
  ticketHistory: ControlHelpHistory[] = [];
  selectedWatcherId = '';
  detailDueAtModel: Date | null = null;
  detailDraft: ControlHelpDetailDraft = {
    status: 'OPEN',
    priority: 'MEDIUM',
    assigned_to: '',
    due_at: null
  };
  private detailOriginalDraft: ControlHelpDetailDraft | null = null;
  private detailOriginalWatchers: ControlHelpWatcher[] = [];
  detailSaving = false;
  assigneeSearch = '';
  watcherSearch = '';
  @ViewChild('detailDuePicker') detailDuePicker?: MatDatepicker<Date>;
  @ViewChild('assigneeSearchInput') assigneeSearchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('watcherSearchInput') watcherSearchInput?: ElementRef<HTMLInputElement>;

  tickets: ControlHelpTicket[] = [];
  categories: ControlHelpCategory[] = [];
  slaPolicies: ControlHelpSla[] = [];
  users: Array<{
    id: number;
    name: string;
    tenant_id?: number | null;
    tenant_name?: string | null;
    department_id?: number | null;
    sector_id?: number | null;
    role_id?: number | null;
    role_name?: string | null;
    is_master?: boolean | number | null;
  }> = [];
  departments: Array<{ id: number; name: string; sector_id?: number | null; sector_name?: string }> = [];
  private allDepartments: Array<{ id: number; name: string; sector_id?: number | null; sector_name?: string }> = [];
  private originDepartmentsList: Array<{ id: number; name: string; sector_id?: number | null; sector_name?: string }> = [];

  newTicketForm = {
    title: '',
    description: '',
    priority: 'MEDIUM' as ControlHelpPriority,
    category: '',
    subcategory: '',
    department_id: '',
    origin_department_id: ''
  };
  newTicketFiles: File[] = [];
  draggingNewTicket = false;
  modalNow: Date = new Date();
  ticketCreateFeedbackState: 'idle' | 'loading' | 'success' | 'error' = 'idle';
  ticketCreateFeedbackMessage = '';
  ticketCreateFeedbackDetail = '';
  private newTicketFilePreviewUrls = new Map<string, string>();

  newCommentForm = {
    comment: '',
    is_internal: false
  };
  commentFile: File | null = null;

  newCategoryForm = {
    department_id: '',
    name: '',
    subcategory: '',
    description: '',
    active: true
  };
  editCategoryForm = {
    department_id: '',
    name: '',
    subcategory: '',
    description: '',
    active: true
  };
  editingCategory: ControlHelpCategory | null = null;

  newSlaForm = {
    department_id: '',
    priority: 'MEDIUM' as ControlHelpPriority,
    response_time_minutes: 60,
    resolution_time_minutes: 240
  };
  editSlaForm = {
    department_id: '',
    priority: 'MEDIUM' as ControlHelpPriority,
    response_time_minutes: 60,
    resolution_time_minutes: 240
  };
  editingSla: ControlHelpSla | null = null;

  constructor(
    private userSession: UserSessionService,
    private controlhelpService: ControlhelpService,
    private userService: UserService,
    private orgStructureService: OrgStructureService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit() {
    const user = this.userSession.getUser();
    this.tenantId = user?.tenant_id || null;
    this.userId = user?.id || null;
    this.userDepartmentId = user?.['department_id'] ?? null;
    this.userSectorId = user?.['sector_id'] ?? user?.['sector']?.id ?? null;
    this.controlhelpDepartmentIds = Array.isArray(user?.['controlhelp_departments'])
      ? user['controlhelp_departments'].map((id: any) => Number(id)).filter((id: number) => Number.isFinite(id))
      : [];
    const roleName = String(user?.role_name ?? user?.role?.role_name ?? user?.role?.name ?? '').toLowerCase();
    const roleId = Number(user?.role_id);
    const hasMasterFlag = Boolean(user?.['is_master'] ?? user?.['isMaster'] ?? user?.['master']);
    this.isMasterUser = hasMasterFlag || roleId === 1 || roleName.includes('master');
    this.isAdminUser = roleName.includes('admin');
    this.isAdminOrMaster = this.isMasterUser || this.isAdminUser;
    this.canManageControlhelp = this.resolveControlhelpManagePermission(user);
    if (this.canManageControlhelp) {
      this.selectedTab = 'dashboard';
    }
    if (this.tenantId) {
      this.fetchUsers();
      this.fetchDepartments();
      this.fetchTickets();
      this.fetchCategories();
      this.fetchSla();
      this.fetchSectorAccess();
      this.checkGuideModal();
    }
    this.refreshDashboardCharts();
  }

  private fetchSectorAccess() {
    const tenantId = this.tenantId;
    if (!tenantId) {
      return;
    }
    this.orgStructureService.getDepartments(tenantId).subscribe({
      next: (departments) => {
        this.allDepartments = departments || [];
        this.orgStructureService.getSectors(tenantId).subscribe({
          next: (rows) => {
            this.controlhelpEnabledSectorIds = (rows || [])
              .filter((sector: any) => !!sector.controlhelp_enabled)
              .map((sector: any) => Number(sector.id))
              .filter((id: number) => Number.isFinite(id));
            this.controlhelpDestinationSectorIds = (rows || [])
              .filter((sector: any) => ['DESTINATION', 'BOTH'].includes(String(sector?.controlhelp_scope || (sector?.controlhelp_enabled ? 'BOTH' : 'NONE')).toUpperCase()))
              .map((sector: any) => Number(sector.id))
              .filter((id: number) => Number.isFinite(id));
            const scopeDepartmentIds = this.resolveControlhelpDepartmentScopeIds();
            if (!scopeDepartmentIds.length) {
              this.controlhelpSectorEnabled = true;
              return;
            }
            const scopeSectorIds = new Set<number>();
            scopeDepartmentIds.forEach((departmentId) => {
              const dept = this.allDepartments.find((d) => Number(d.id) === Number(departmentId));
              if (dept?.sector_id) {
                scopeSectorIds.add(Number(dept.sector_id));
              }
            });
            if (!scopeSectorIds.size) {
              this.controlhelpSectorEnabled = true;
              return;
            }
            this.controlhelpSectorEnabled = (rows || []).some((sector: any) => {
              return scopeSectorIds.has(Number(sector.id)) && ['DESTINATION', 'BOTH'].includes(String(sector?.controlhelp_scope || (sector?.controlhelp_enabled ? 'BOTH' : 'NONE')).toUpperCase());
            });
            if (!this.controlhelpSectorEnabled) {
              this.selectedTab = 'tickets';
              this.ticketView = 'mine';
            }
          },
          error: () => {
            this.controlhelpEnabledSectorIds = null;
            this.controlhelpDestinationSectorIds = null;
            this.controlhelpSectorEnabled = true;
          }
        });
      },
      error: () => {
        this.controlhelpEnabledSectorIds = null;
        this.controlhelpDestinationSectorIds = null;
        this.controlhelpSectorEnabled = true;
      }
    });
  }

  private checkGuideModal() {
    const key = this.guideStorageKey();
    if (!key) {
      return;
    }
    try {
      this.showGuideModal = !localStorage.getItem(key);
    } catch (_) {
      this.showGuideModal = true;
    }
  }

  closeGuideModal() {
    this.showGuideModal = false;
    const key = this.guideStorageKey();
    if (!key) {
      return;
    }
    try {
      localStorage.setItem(key, '1');
    } catch (_) {}
  }

  openGuideTab(tab: 'tickets' | 'categories' | 'sla') {
    this.selectedTab = tab;
  }

  selectMainTab(tab: 'dashboard' | 'tickets' | 'categories' | 'sla') {
    if (tab === 'dashboard' && !this.canManageControlhelp) {
      return;
    }
    this.selectedTab = tab;
    if (tab === 'tickets') {
      this.ticketPage = 1;
    }
  }

  setTicketView(view: 'mine' | 'queue') {
    this.ticketView = view;
    this.ticketPage = 1;
  }

  onTicketFiltersChanged() {
    this.ticketPage = 1;
  }

  private guideStorageKey() {
    if (!this.tenantId) {
      return null;
    }
    return `controlhelp-guide-seen-${this.tenantId}-${this.userId ?? 'anon'}`;
  }

  private resolveControlhelpDepartmentScopeIds() {
    const explicit = Array.from(new Set(this.controlhelpDepartmentIds.map((id) => Number(id)).filter((id) => Number.isFinite(id))));
    if (explicit.length) {
      return explicit;
    }
    if (this.isMasterUser || this.isAdminUser) {
      return [];
    }
    if (this.userDepartmentId) {
      return [Number(this.userDepartmentId)];
    }
    return [];
  }

  private getControlhelpScopeMode(): 'all' | 'departments' | 'personal' {
    if (this.controlhelpDepartmentIds.length) {
      return 'departments';
    }
    if (this.isMasterUser || this.isAdminUser) {
      return 'all';
    }
    if (this.userDepartmentId) {
      return 'departments';
    }
    return 'personal';
  }

  private getControlhelpScopeDepartmentIdsForFilters() {
    const mode = this.getControlhelpScopeMode();
    if (mode === 'all') {
      const source = this.departments.length ? this.departments : this.allDepartments;
      return Array.from(new Set(source.map((dept) => Number(dept.id)).filter((id) => Number.isFinite(id))));
    }
    if (mode === 'departments') {
      return this.resolveControlhelpDepartmentScopeIds();
    }
    return [];
  }

  openNewTicket() {
    this.ticketFormFeedback = '';
    this.showNewTicketModal = true;
    this.newTicketStep = 1;
    this.modalNow = new Date();
    this.ticketCreateFeedbackState = 'idle';
    this.ticketCreateFeedbackMessage = '';
    this.ticketCreateFeedbackDetail = '';
    if (!this.newTicketForm.origin_department_id) {
      const defaultDepartmentId = this.getDefaultControlhelpOriginDepartmentId();
      if (defaultDepartmentId) {
        this.newTicketForm.origin_department_id = String(defaultDepartmentId);
      }
    }
  }

  closeNewTicket() {
    this.showNewTicketModal = false;
    this.resetNewTicketForm();
    this.clearNewTicketFilePreviews();
    this.newTicketFiles = [];
    this.draggingNewTicket = false;
    this.ticketFormFeedback = '';
    this.newTicketStep = 1;
    this.ticketCreateFeedbackState = 'idle';
    this.ticketCreateFeedbackMessage = '';
    this.ticketCreateFeedbackDetail = '';
  }

  openTicketDetail(ticket: ControlHelpTicket) {
    this.activeTicket = ticket;
    this.detailDraft = this.buildDetailDraft(ticket);
    this.detailOriginalDraft = { ...this.detailDraft };
    this.showTicketDetail = true;
    this.ticketDetailTab = 'summary';
    this.detailPanelOpen = {
      summary: false,
      statusPriority: false,
      assigneeDue: false,
      watchers: false
    };
    this.detailFeedback = '';
    this.ticketWatchers = [];
    this.detailOriginalWatchers = [];
    this.selectedWatcherId = '';
    this.assigneeSearch = '';
    this.watcherSearch = '';
    this.detailSaving = false;
    this.detailDueAtModel = this.toDateValue(ticket.due_at);
    if (this.tenantId) {
      this.fetchComments(ticket.id);
      this.fetchAttachments(ticket.id);
      this.fetchHistory(ticket.id);
      this.fetchWatchers(ticket.id);
    }
  }

  closeTicketDetail() {
    if (this.detailSaving) {
      return;
    }
    this.showTicketDetail = false;
    this.activeTicket = null;
    this.ticketDetailTab = 'summary';
    this.detailFeedback = '';
    this.ticketWatchers = [];
    this.detailOriginalWatchers = [];
    this.detailOriginalDraft = null;
    this.selectedWatcherId = '';
    this.assigneeSearch = '';
    this.watcherSearch = '';
    this.detailSaving = false;
    this.detailDueAtModel = null;
  }

  submitTicket() {
    this.ticketFormFeedback = '';
    if (this.ticketCreateFeedbackState === 'loading') {
      return;
    }
    if (!this.newTicketForm.title.trim()) {
      this.setTicketFormFeedback('error', 'Informe o título do chamado.');
      return;
    }
    if (!this.newTicketForm.description.trim()) {
      this.setTicketFormFeedback('error', 'Informe a descrição do chamado.');
      return;
    }
    if (!this.newTicketForm.origin_department_id) {
      this.setTicketFormFeedback('error', 'Selecione a origem do chamado.');
      return;
    }
    if (!this.newTicketForm.department_id) {
      this.setTicketFormFeedback('error', 'Selecione um departamento.');
      return;
    }
    if (!this.tenantId || !this.userId) {
      this.setTicketFormFeedback('error', 'Não foi possível identificar o usuário.');
      return;
    }
    this.ticketCreateFeedbackState = 'loading';
    this.ticketCreateFeedbackMessage = 'Criando chamado...';
    this.ticketCreateFeedbackDetail = 'Aguarde enquanto registramos o ticket e enviamos a confirmação.';
    const payload = {
      tenant_id: this.tenantId,
      created_by: this.userId,
      title: this.newTicketForm.title,
      description: this.newTicketForm.description,
      priority: this.newTicketForm.priority,
      category: this.newTicketForm.category || null,
      subcategory: this.newTicketForm.subcategory || null,
      department_id: Number(this.newTicketForm.department_id),
      origin_department_id: this.newTicketForm.origin_department_id
        ? Number(this.newTicketForm.origin_department_id)
        : null
    };
    this.controlhelpService.createTicket(payload).subscribe({
      next: (created) => {
        const concludeSuccess = (attachmentsOk: boolean) => {
          this.tickets = [created, ...this.tickets];
          const emailSent = created?.email_sent !== false;
          this.ticketCreateFeedbackState = 'success';
          this.ticketCreateFeedbackMessage = 'Chamado criado com sucesso!';
          if (attachmentsOk && emailSent) {
            this.ticketCreateFeedbackDetail = 'Seu chamado foi aberto e o e-mail de confirmação já foi enviado.';
            return;
          }
          if (!attachmentsOk && emailSent) {
            this.ticketCreateFeedbackDetail = 'Chamado criado, mas houve falha no envio de alguns anexos.';
            return;
          }
          if (attachmentsOk && !emailSent) {
            this.ticketCreateFeedbackDetail = 'Chamado criado, mas não foi possível enviar o e-mail de confirmação.';
            return;
          }
          this.ticketCreateFeedbackDetail =
            'Chamado criado, mas houve falha no envio dos anexos e no e-mail de confirmação.';
        };

        if (this.newTicketFiles.length) {
          this.uploadNewTicketAttachments(created.id).subscribe((success) => {
            concludeSuccess(success);
          });
          return;
        }
        concludeSuccess(true);
      },
      error: (err) => {
        const message = err?.error?.error || 'Não foi possível abrir o chamado.';
        this.setTicketFormFeedback('error', message);
        this.ticketCreateFeedbackState = 'error';
        this.ticketCreateFeedbackMessage = 'Não foi possível criar o chamado.';
        this.ticketCreateFeedbackDetail = message;
      }
    });
  }

  addComment() {
    if (!this.newCommentForm.comment.trim() || !this.activeTicket) {
      return;
    }
    if (!this.tenantId || !this.userId) {
      return;
    }
    const payload = {
      tenant_id: this.tenantId,
      user_id: this.userId,
      comment: this.newCommentForm.comment.trim(),
      is_internal: this.canCreateInternalComment() && this.newCommentForm.is_internal
    };
    this.controlhelpService.addComment(this.activeTicket.id, payload).subscribe({
      next: (created) => {
        this.newCommentForm = { comment: '', is_internal: this.canCreateInternalComment() && this.newCommentForm.is_internal };
        this.snackBar.open('Comentário enviado.', 'OK', { duration: 2500 });
        if (created?.id && this.commentFile) {
          const formData = new FormData();
          formData.append('tenant_id', String(this.tenantId));
          formData.append('uploaded_by', String(this.userId));
          formData.append('comment_id', String(created.id));
          formData.append('file', this.commentFile);
          this.controlhelpService.uploadAttachment(this.activeTicket!.id, formData, this.tenantId ?? undefined).subscribe({
            next: () => {
              this.fetchAttachments(this.activeTicket!.id);
              this.fetchHistory(this.activeTicket!.id);
            },
            error: () => {
              this.setDetailFeedback('error', 'Não foi possível enviar o anexo do comentário.');
            }
          });
        }
        this.commentFile = null;
        this.fetchComments(this.activeTicket!.id);
        this.fetchHistory(this.activeTicket!.id);
      }
    });
  }

  addAttachment(event: Event) {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0];
    if (!file) {
      return;
    }
    if (!this.tenantId || !this.userId || !this.activeTicket) {
      return;
    }
    const formData = new FormData();
    formData.append('tenant_id', String(this.tenantId));
    formData.append('uploaded_by', String(this.userId));
    formData.append('file', file);
    this.controlhelpService.uploadAttachment(this.activeTicket.id, formData, this.tenantId ?? undefined).subscribe({
      next: () => {
        this.fetchAttachments(this.activeTicket!.id);
        this.fetchHistory(this.activeTicket!.id);
      },
      error: () => {
        this.setDetailFeedback('error', 'Não foi possível enviar o anexo.');
      }
    });
    input.value = '';
  }

  onCommentFileSelected(event: Event) {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0] || null;
    this.commentFile = file;
    if (input) {
      input.value = '';
    }
  }

  onNewTicketDragOver(event: DragEvent) {
    event.preventDefault();
    this.draggingNewTicket = true;
  }

  onNewTicketDragLeave() {
    this.draggingNewTicket = false;
  }

  onNewTicketDrop(event: DragEvent) {
    event.preventDefault();
    this.draggingNewTicket = false;
    const files = Array.from(event.dataTransfer?.files || []);
    if (files.length) {
      this.newTicketFiles = [...this.newTicketFiles, ...files];
    }
  }

  onNewTicketFileSelect(event: Event) {
    const input = event.target as HTMLInputElement | null;
    const files = Array.from(input?.files || []);
    if (files.length) {
      this.newTicketFiles = [...this.newTicketFiles, ...files];
    }
    if (input) {
      input.value = '';
    }
  }

  removeNewTicketFile(index: number) {
    const file = this.newTicketFiles[index];
    if (file) {
      this.revokeNewTicketFilePreview(file);
    }
    this.newTicketFiles = this.newTicketFiles.filter((_item, idx) => idx !== index);
  }

  isImageFile(file: File) {
    return /^image\//i.test(file.type || '') || /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(file.name);
  }

  isPdfFile(file: File) {
    return /pdf/i.test(file.type || '') || /\.pdf$/i.test(file.name);
  }

  isImageAttachment(file: ControlHelpAttachment) {
    const type = String(file.file_type || '').toLowerCase();
    return type.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(String(file.file_name || ''));
  }

  isPdfAttachment(file: ControlHelpAttachment) {
    const type = String(file.file_type || '').toLowerCase();
    return type.includes('pdf') || /\.pdf$/i.test(String(file.file_name || ''));
  }

  getNewTicketFilePreview(file: File) {
    if (!this.isImageFile(file)) {
      return '';
    }
    const key = this.newTicketFileKey(file);
    const cached = this.newTicketFilePreviewUrls.get(key);
    if (cached) {
      return cached;
    }
    const url = URL.createObjectURL(file);
    this.newTicketFilePreviewUrls.set(key, url);
    return url;
  }

  onDueAtPickerChange(value: Date | null) {
    if (!value) {
      this.detailDueAtModel = null;
      this.detailDraft.due_at = null;
      return;
    }
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    this.detailDueAtModel = new Date(year, Number(month) - 1, Number(day));
    this.detailDraft.due_at = `${year}-${month}-${day}`;
  }

  hasPendingDetailChanges() {
    if (!this.detailOriginalDraft) {
      return false;
    }
    const ticketChanged =
      this.detailDraft.status !== this.detailOriginalDraft.status
      || this.detailDraft.priority !== this.detailOriginalDraft.priority
      || String(this.detailDraft.assigned_to || '') !== String(this.detailOriginalDraft.assigned_to || '')
      || String(this.detailDraft.due_at || '') !== String(this.detailOriginalDraft.due_at || '');
    return ticketChanged || this.haveWatcherIdsChanged();
  }

  discardDetailChanges() {
    if (!this.detailOriginalDraft || this.detailSaving) {
      return;
    }
    this.detailDraft = { ...this.detailOriginalDraft };
    this.detailDueAtModel = this.toDateValue(this.detailDraft.due_at);
    this.ticketWatchers = this.detailOriginalWatchers.map((watcher) => ({ ...watcher }));
    this.selectedWatcherId = '';
    this.assigneeSearch = '';
    this.watcherSearch = '';
    this.detailFeedback = '';
  }

  saveTicketDetailChanges() {
    if (
      !this.activeTicket
      || !this.detailOriginalDraft
      || !this.tenantId
      || !this.userId
      || this.detailSaving
      || !this.hasPendingDetailChanges()
    ) {
      return;
    }

    const ticketPayload: Record<string, unknown> = {
      tenant_id: this.tenantId,
      user_id: this.userId
    };
    if (this.canEditActiveTicket() && this.detailDraft.status !== this.detailOriginalDraft.status) {
      ticketPayload['status'] = this.detailDraft.status;
    }
    if (this.canEditAssigneeAndPriority()) {
      if (this.detailDraft.priority !== this.detailOriginalDraft.priority) {
        ticketPayload['priority'] = this.detailDraft.priority;
      }
      if (String(this.detailDraft.assigned_to || '') !== String(this.detailOriginalDraft.assigned_to || '')) {
        ticketPayload['assigned_to'] = this.detailDraft.assigned_to ? Number(this.detailDraft.assigned_to) : null;
      }
    }
    if (
      this.canEditActiveTicket()
      && String(this.detailDraft.due_at || '') !== String(this.detailOriginalDraft.due_at || '')
    ) {
      ticketPayload['due_at'] = this.detailDraft.due_at;
    }

    const ticketChanged = Object.keys(ticketPayload).length > 2;
    const watchersChanged = this.canEditActiveTicket() && this.haveWatcherIdsChanged();
    const ticketRequest = ticketChanged
      ? this.controlhelpService.updateTicket(this.activeTicket.id, ticketPayload)
      : of(this.activeTicket);
    const watcherRequest = watchersChanged
      ? this.controlhelpService.setWatchers(this.activeTicket.id, {
          tenant_id: this.tenantId,
          user_id: this.userId,
          watcher_ids: this.currentWatcherIds()
        })
      : of(this.ticketWatchers);

    this.detailSaving = true;
    this.detailFeedback = '';
    forkJoin({ ticket: ticketRequest, watchers: watcherRequest }).subscribe({
      next: ({ ticket, watchers }) => {
        const updatedTicket = ticket as ControlHelpTicket;
        this.activeTicket = updatedTicket;
        this.tickets = this.tickets.map((row) => (row.id === updatedTicket.id ? updatedTicket : row));
        this.ticketWatchers = (watchers || []).map((watcher: ControlHelpWatcher) => ({ ...watcher }));
        this.detailOriginalWatchers = this.ticketWatchers.map((watcher) => ({ ...watcher }));
        this.detailDraft = this.buildDetailDraft(updatedTicket);
        this.detailOriginalDraft = { ...this.detailDraft };
        this.detailDueAtModel = this.toDateValue(updatedTicket.due_at);
        this.detailSaving = false;
        this.fetchHistory(updatedTicket.id);
        this.refreshDashboardCharts();
        this.setDetailFeedback('success', 'Alterações salvas com sucesso.');
      },
      error: () => {
        this.detailSaving = false;
        this.fetchTickets();
        this.fetchWatchers(this.activeTicket!.id);
        this.fetchHistory(this.activeTicket!.id);
        this.setDetailFeedback('error', 'Não foi possível salvar todas as alterações. Revise os campos e tente novamente.');
      }
    });
  }

  toggleDetailPanel(section: 'summary' | 'statusPriority' | 'assigneeDue' | 'watchers') {
    this.detailPanelOpen = {
      ...this.detailPanelOpen,
      [section]: !this.detailPanelOpen[section]
    };
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.showTicketDetail) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }
    const clickedInsideDuePicker = !!(
      target.closest('.controlhelp-due-date-field')
      || target.closest('.cdk-overlay-pane')
      || target.closest('.mat-mdc-datepicker-content')
      || target.closest('.mat-datepicker-content')
      || target.closest('.mat-datepicker-toggle')
      || target.closest('.mat-calendar')
      || target.closest('.mat-calendar-body')
      || target.closest('.mat-calendar-body-cell')
    );
    if (!clickedInsideDuePicker) {
      this.detailDuePicker?.close();
    }
  }

  addWatcher(userId: string | number) {
    const id = Number(userId);
    if (!id || !this.activeTicket || !this.canEditActiveTicket()) {
      this.selectedWatcherId = '';
      return;
    }

    const alreadyAdded = this.ticketWatchers.some((watcher) => Number(watcher.user_id) === id);
    if (alreadyAdded) {
      this.selectedWatcherId = '';
      return;
    }

    const user = this.users.find((row) => Number(row.id) === id);
    this.ticketWatchers = [
      ...this.ticketWatchers,
      { user_id: id, user_name: user?.name || this.userNameById(id) }
    ];
    this.selectedWatcherId = '';
    this.watcherSearch = '';
    this.detailFeedback = '';
  }

  removeWatcher(userId: number) {
    if (!this.activeTicket || !this.canEditActiveTicket()) {
      return;
    }
    this.ticketWatchers = this.ticketWatchers.filter(
      (watcher) => Number(watcher.user_id) !== Number(userId)
    );
    this.detailFeedback = '';
  }

  filteredAssigneeUsers() {
    const term = this.normalizePeopleSearch(this.assigneeSearch);
    const selectedId = Number(this.detailDraft.assigned_to || 0);
    return this.usersForAssignedTicket().filter((user) => {
      if (Number(user.id) === selectedId) {
        return true;
      }
      return !term || this.normalizePeopleSearch(user.name).includes(term);
    });
  }

  onAssigneeSelectOpened(opened: boolean) {
    if (!opened) {
      this.assigneeSearch = '';
      return;
    }
    window.setTimeout(() => this.assigneeSearchInput?.nativeElement.focus(), 0);
  }

  onWatcherSelectOpened(opened: boolean) {
    if (!opened) {
      this.watcherSearch = '';
      return;
    }
    window.setTimeout(() => this.watcherSearchInput?.nativeElement.focus(), 0);
  }

  private buildDetailDraft(ticket: ControlHelpTicket): ControlHelpDetailDraft {
    return {
      status: ticket.status,
      priority: ticket.priority,
      assigned_to: ticket.assigned_to ? String(ticket.assigned_to) : '',
      due_at: this.formatDateInput(ticket.due_at) || null
    };
  }

  private currentWatcherIds() {
    return this.ticketWatchers
      .map((watcher) => Number(watcher.user_id))
      .filter((id) => Number.isFinite(id) && id > 0)
      .sort((left, right) => left - right);
  }

  private originalWatcherIds() {
    return this.detailOriginalWatchers
      .map((watcher) => Number(watcher.user_id))
      .filter((id) => Number.isFinite(id) && id > 0)
      .sort((left, right) => left - right);
  }

  private haveWatcherIdsChanged() {
    const current = this.currentWatcherIds();
    const original = this.originalWatcherIds();
    return current.length !== original.length || current.some((id, index) => id !== original[index]);
  }

  private normalizePeopleSearch(value: string | null | undefined) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase();
  }

  openCategoryModal() {
    if (this.userDepartmentId && !this.isMasterUser) {
      this.newCategoryForm.department_id = String(this.userDepartmentId);
    }
    this.showNewCategoryModal = true;
  }

  closeCategoryModal() {
    this.showNewCategoryModal = false;
    this.newCategoryForm = { department_id: '', name: '', subcategory: '', description: '', active: true };
  }

  openEditCategoryModal(category: ControlHelpCategory) {
    this.editingCategory = category;
    this.editCategoryForm = {
      department_id: String(category.department_id ?? ''),
      name: category.name || '',
      subcategory: category.subcategory || '',
      description: category.description || '',
      active: category.active
    };
    this.showEditCategoryModal = true;
  }

  closeEditCategoryModal() {
    this.showEditCategoryModal = false;
    this.editingCategory = null;
  }

  submitCategory() {
    if (!this.newCategoryForm.department_id) {
      return;
    }
    if (!this.newCategoryForm.name.trim()) {
      return;
    }
    if (!this.tenantId) {
      return;
    }
    const payload = {
      tenant_id: this.tenantId,
      department_id: Number(this.newCategoryForm.department_id),
      name: this.newCategoryForm.name.trim(),
      subcategory: this.newCategoryForm.subcategory.trim() || null,
      description: this.newCategoryForm.description.trim(),
      active: this.newCategoryForm.active
    };
    this.controlhelpService.createCategory(payload).subscribe({
      next: (created) => {
        this.categories = [created, ...this.categories];
        this.closeCategoryModal();
      }
    });
  }

  submitCategoryEdit() {
    if (!this.editingCategory || !this.tenantId) {
      return;
    }
    if (!this.editCategoryForm.department_id) {
      return;
    }
    if (!this.editCategoryForm.name.trim()) {
      return;
    }
    const payload = {
      tenant_id: this.tenantId,
      department_id: Number(this.editCategoryForm.department_id),
      name: this.editCategoryForm.name.trim(),
      subcategory: this.editCategoryForm.subcategory.trim() || null,
      description: this.editCategoryForm.description.trim(),
      active: this.editCategoryForm.active
    };
    this.controlhelpService.updateCategory(this.editingCategory.id, payload).subscribe({
      next: (updated) => {
        this.categories = this.categories.map((c) => (c.id === updated.id ? updated : c));
        this.closeEditCategoryModal();
      }
    });
  }

  openSlaModal() {
    if (this.userDepartmentId && !this.isMasterUser) {
      this.newSlaForm.department_id = String(this.userDepartmentId);
    }
    this.showNewSlaModal = true;
  }

  closeSlaModal() {
    this.showNewSlaModal = false;
    this.newSlaForm = { department_id: '', priority: 'MEDIUM', response_time_minutes: 60, resolution_time_minutes: 240 };
  }

  openEditSlaModal(sla: ControlHelpSla) {
    this.editingSla = sla;
    this.editSlaForm = {
      department_id: String(sla.department_id ?? ''),
      priority: sla.priority,
      response_time_minutes: sla.response_time_minutes,
      resolution_time_minutes: sla.resolution_time_minutes
    };
    this.showEditSlaModal = true;
  }

  closeEditSlaModal() {
    this.showEditSlaModal = false;
    this.editingSla = null;
  }

  submitSla() {
    if (!this.tenantId) {
      return;
    }
    if (!this.newSlaForm.department_id) {
      return;
    }
    const payload = {
      tenant_id: this.tenantId,
      department_id: Number(this.newSlaForm.department_id),
      priority: this.newSlaForm.priority,
      response_time_minutes: this.newSlaForm.response_time_minutes,
      resolution_time_minutes: this.newSlaForm.resolution_time_minutes
    };
    this.controlhelpService.createSla(payload).subscribe({
      next: (created) => {
        this.slaPolicies = [created, ...this.slaPolicies];
        this.closeSlaModal();
      }
    });
  }

  submitSlaEdit() {
    if (!this.editingSla || !this.tenantId) {
      return;
    }
    if (!this.editSlaForm.department_id) {
      return;
    }
    const payload = {
      tenant_id: this.tenantId,
      department_id: Number(this.editSlaForm.department_id),
      priority: this.editSlaForm.priority,
      response_time_minutes: this.editSlaForm.response_time_minutes,
      resolution_time_minutes: this.editSlaForm.resolution_time_minutes
    };
    this.controlhelpService.updateSla(this.editingSla.id, payload).subscribe({
      next: (updated) => {
        this.slaPolicies = this.slaPolicies.map((s) => (s.id === updated.id ? updated : s));
        this.closeEditSlaModal();
      }
    });
  }

  get filteredTickets() {
    return this.applyTicketSort(
      this.applyTicketFilters(this.ticketsForView(), { hideClosedByDefault: true })
    );
  }

  get ticketDepartmentFilterOptions() {
    const scopeMode = this.getControlhelpScopeMode();
    const scopeDepartmentIds = this.getControlhelpScopeDepartmentIdsForFilters();
    const source = this.departments.length ? this.departments : this.allDepartments;
    const list = source
      .filter((dept) => {
        if (scopeMode === 'all') {
          return true;
        }
        if (!scopeDepartmentIds.length) {
          return false;
        }
        return scopeDepartmentIds.includes(Number(dept.id));
      })
      .slice()
      .sort((a, b) => {
        const labelA = a.sector_name ? `${a.sector_name} / ${a.name}` : a.name;
        const labelB = b.sector_name ? `${b.sector_name} / ${b.name}` : b.name;
        return String(labelA).localeCompare(String(labelB));
      });
    return list;
  }

  get pagedTickets() {
    const start = (this.ticketPage - 1) * this.ticketPageSize;
    return this.filteredTickets.slice(start, start + this.ticketPageSize);
  }

  get ticketTotalPages() {
    const total = Math.ceil(this.filteredTickets.length / this.ticketPageSize);
    return Math.max(total, 1);
  }

  get ticketPageStart() {
    if (!this.filteredTickets.length) {
      return 0;
    }
    return (this.ticketPage - 1) * this.ticketPageSize + 1;
  }

  get ticketPageEnd() {
    return Math.min(this.ticketPage * this.ticketPageSize, this.filteredTickets.length);
  }

  prevTicketPage() {
    if (this.ticketPage <= 1) {
      return;
    }
    this.ticketPage -= 1;
  }

  nextTicketPage() {
    if (this.ticketPage >= this.ticketTotalPages) {
      return;
    }
    this.ticketPage += 1;
  }

  get filteredCategories() {
    const categoryTerm = (this.categorySearch || '').toLowerCase();
    const subcategoryTerm = (this.subcategorySearch || '').toLowerCase();
    const scopeDepartmentIds = this.getControlhelpScopeDepartmentIdsForFilters();
    return this.categories.filter((category) => {
      if (scopeDepartmentIds.length && !scopeDepartmentIds.includes(Number(category.department_id))) {
        return false;
      }
      if (!scopeDepartmentIds.length && !this.isMasterUser && this.userDepartmentId) {
        if (Number(category.department_id) !== Number(this.userDepartmentId)) {
          return false;
        }
      }
      if (categoryTerm && !(category.name || '').toLowerCase().includes(categoryTerm)) {
        return false;
      }
      if (subcategoryTerm && !(category.subcategory || '').toLowerCase().includes(subcategoryTerm)) {
        return false;
      }
      return true;
    });
  }

  get filteredSlaPolicies() {
    const categoryTerm = (this.slaCategorySearch || '').toLowerCase();
    const subcategoryTerm = (this.slaSubcategorySearch || '').toLowerCase();
    const scopeDepartmentIds = this.getControlhelpScopeDepartmentIdsForFilters();
    return this.slaPolicies.filter((sla) => {
      if (scopeDepartmentIds.length && !scopeDepartmentIds.includes(Number(sla.department_id))) {
        return false;
      }
      if (!scopeDepartmentIds.length && !this.isMasterUser && this.userDepartmentId) {
        if (Number(sla.department_id) !== Number(this.userDepartmentId)) {
          return false;
        }
      }
      const priorityLabel = this.formatPriority(sla.priority).toLowerCase();
      const deptLabel = this.departmentLabel(sla.department_id).toLowerCase();
      if (categoryTerm && !priorityLabel.includes(categoryTerm)) {
        return false;
      }
      if (subcategoryTerm && !deptLabel.includes(subcategoryTerm)) {
        return false;
      }
      return true;
    });
  }

  ticketsByStatus(status: ControlHelpStatus) {
    return this.filteredSummaryTickets().filter((t) => t.status === status).length;
  }

  setDashboardPeriod(months: number) {
    this.dashboardPeriodMonths = months;
    this.refreshDashboardCharts();
  }

  get dashboardScopedTickets() {
    return this.ticketsForSummary();
  }

  get dashboardOpenCount() {
    return this.dashboardScopedTickets.filter((t) => t.status === 'OPEN').length;
  }

  get dashboardInProgressCount() {
    return this.dashboardScopedTickets.filter((t) => t.status === 'IN_PROGRESS').length;
  }

  get dashboardWaitingCount() {
    return this.dashboardScopedTickets.filter((t) => t.status === 'WAITING_USER' || t.status === 'WAITING_THIRD_PARTY').length;
  }

  get dashboardResolvedCount() {
    return this.dashboardScopedTickets.filter((t) => t.status === 'RESOLVED').length;
  }

  get dashboardTicketsByMonth() {
    const months = Math.max(1, Number(this.dashboardPeriodMonths || 12));
    const start = new Date();
    start.setDate(1);
    start.setHours(0, 0, 0, 0);
    start.setMonth(start.getMonth() - (months - 1));

    const buckets: Array<{ key: string; label: string; opened: number; resolved: number }> = [];
    const cursor = new Date(start);
    for (let i = 0; i < months; i += 1) {
      const key = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`;
      const monthLabel = cursor
        .toLocaleDateString('pt-BR', { month: 'short' })
        .replace('.', '')
        .trim();
      const yearLabel = String(cursor.getFullYear()).slice(-2);
      const label = `${monthLabel.charAt(0).toUpperCase()}${monthLabel.slice(1)}/${yearLabel}`;
      buckets.push({ key, label, opened: 0, resolved: 0 });
      cursor.setMonth(cursor.getMonth() + 1);
    }

    const bucketMap = new Map<string, { key: string; label: string; opened: number; resolved: number }>();
    buckets.forEach((bucket) => bucketMap.set(bucket.key, bucket));

    this.dashboardScopedTickets.forEach((ticket) => {
      const openedDate = this.toValidDate(ticket.opened_at);
      if (openedDate) {
        const openedKey = `${openedDate.getFullYear()}-${String(openedDate.getMonth() + 1).padStart(2, '0')}`;
        const openedBucket = bucketMap.get(openedKey);
        if (openedBucket) {
          openedBucket.opened += 1;
        }
      }
      const resolvedDate = this.toValidDate(ticket.closed_at || ticket.last_action_at);
      if (resolvedDate && (ticket.status === 'RESOLVED' || ticket.status === 'CLOSED')) {
        const resolvedKey = `${resolvedDate.getFullYear()}-${String(resolvedDate.getMonth() + 1).padStart(2, '0')}`;
        const resolvedBucket = bucketMap.get(resolvedKey);
        if (resolvedBucket) {
          resolvedBucket.resolved += 1;
        }
      }
    });

    return buckets;
  }

  get dashboardTicketsOpenedSeries() {
    return this.dashboardTicketsByMonth.map((item) => item.opened);
  }

  get dashboardTicketsResolvedSeries() {
    return this.dashboardTicketsByMonth.map((item) => item.resolved);
  }

  get dashboardTicketsAverage() {
    const values = this.dashboardTicketsOpenedSeries;
    if (!values.length) {
      return 0;
    }
    const total = values.reduce((sum, value) => sum + value, 0);
    return Number((total / values.length).toFixed(2));
  }

  get dashboardTicketsAverageLabel() {
    return this.dashboardTicketsAverage.toLocaleString('pt-BR', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    });
  }

  private buildDashboardLineChartData(): ChartData<'line'> {
    const labels = this.dashboardTicketsByMonth.map((item) => item.label);
    const opened = this.dashboardTicketsOpenedSeries;
    const resolved = this.dashboardTicketsResolvedSeries;
    const avg = opened.map(() => Number(this.dashboardTicketsAverage.toFixed(1)));
    return {
      labels,
      datasets: [
        {
          label: 'Abertos',
          data: opened,
          borderColor: '#22b8b3',
          backgroundColor: 'rgba(34, 184, 179, 0.22)',
          pointBackgroundColor: '#22b8b3',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
          borderWidth: 3,
          fill: true,
          tension: 0.35
        },
        {
          label: 'Resolvidos',
          data: resolved.map((value) => Number(value || 0)),
          borderColor: '#efc44f',
          backgroundColor: 'rgba(239, 196, 79, 0.12)',
          pointBackgroundColor: '#efc44f',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          pointRadius: 3,
          pointHoverRadius: 6,
          borderWidth: 2.5,
          fill: false,
          tension: 0.35
        },
        {
          label: `Média ${this.dashboardTicketsAverageLabel}/mês`,
          data: avg,
          borderColor: '#91a9c5',
          borderWidth: 2,
          borderDash: [6, 6],
          pointRadius: 0,
          pointHoverRadius: 0,
          fill: false,
          tension: 0
        }
      ]
    };
  }

  get dashboardPriorityRows() {
    const palette = ['#22b8b3', '#4f7cf5', '#f4b942', '#e76868'];
    const labels: Array<{ key: ControlHelpPriority; label: string }> = [
      { key: 'LOW', label: 'Baixa' },
      { key: 'MEDIUM', label: 'Média' },
      { key: 'HIGH', label: 'Alta' },
      { key: 'URGENT', label: 'Urgente' }
    ];
    const total = this.dashboardScopedTickets.length || 1;
    return labels
      .map((item, index) => {
        const count = this.dashboardScopedTickets.filter((ticket) => ticket.priority === item.key).length;
        const percent = Number(((count / total) * 100).toFixed(1));
        return { ...item, count, percent, color: palette[index % palette.length] };
      })
      .sort((a, b) => b.count - a.count);
  }

  private buildDashboardPriorityDonutData(): ChartData<'doughnut'> {
    const rows = this.dashboardPriorityRows;
    const labels = rows.map((row) => row.label);
    const values = rows.map((row) => row.count);
    const colors = rows.map((row) => row.color);
    return {
      labels,
      datasets: [
        {
          data: values.length ? values : [1],
          backgroundColor: values.length ? colors : ['#e7eef8'],
          borderWidth: 0,
          hoverOffset: 6
        }
      ]
    };
  }

  private refreshDashboardCharts() {
    this.dashboardLineChartData = this.buildDashboardLineChartData();
    this.dashboardPriorityDonutData = this.buildDashboardPriorityDonutData();
  }

  get dashboardPriorityTop() {
    return this.dashboardPriorityRows[0] || { label: 'Sem dados', count: 0 };
  }

  get dashboardStatusRows() {
    const total = this.dashboardScopedTickets.length || 1;
    const rows: Array<{ label: string; count: number; percent: number; tone: string }> = [
      { label: 'Aberto', count: this.dashboardOpenCount, percent: 0, tone: 'open' },
      { label: 'Em andamento', count: this.dashboardInProgressCount, percent: 0, tone: 'progress' },
      { label: 'Aguardando', count: this.dashboardWaitingCount, percent: 0, tone: 'waiting' },
      { label: 'Resolvido', count: this.dashboardResolvedCount, percent: 0, tone: 'resolved' }
    ];
    return rows.map((row) => ({ ...row, percent: Number(((row.count / total) * 100).toFixed(1)) }));
  }

  get dashboardOpenedByDepartmentRows() {
    const counts = new Map<string, number>();
    this.dashboardScopedTickets.forEach((ticket) => {
      const creator = this.users.find((user) => Number(user.id) === Number(ticket.created_by));
      const creatorDeptId = creator?.department_id ?? null;
      let label = 'Não informado';
      if (creatorDeptId) {
        const dept =
          this.allDepartments.find((item) => Number(item.id) === Number(creatorDeptId)) ||
          this.departments.find((item) => Number(item.id) === Number(creatorDeptId));
        if (dept?.name) {
          label = dept.sector_name ? `${dept.sector_name} / ${dept.name}` : dept.name;
        }
      }
      counts.set(label, (counts.get(label) || 0) + 1);
    });
    const rows = Array.from(counts.entries()).map(([label, count]) => ({ label, count }));
    const total = rows.reduce((sum, row) => sum + row.count, 0) || 1;
    return rows
      .map((row) => ({
        ...row,
        percent: Number(((row.count / total) * 100).toFixed(1))
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);
  }

  get dashboardOpenedByUserRows() {
    const counts = new Map<string, number>();
    this.dashboardScopedTickets.forEach((ticket) => {
      const byName = String(ticket.created_by_name || '').trim();
      const label = byName || this.userNameById(ticket.created_by);
      counts.set(label, (counts.get(label) || 0) + 1);
    });
    const rows = Array.from(counts.entries()).map(([label, count]) => ({ label, count }));
    const total = rows.reduce((sum, row) => sum + row.count, 0) || 1;
    return rows
      .map((row) => ({
        ...row,
        percent: Number(((row.count / total) * 100).toFixed(1))
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);
  }

  ticketViewLabel() {
    return this.ticketView === 'queue' ? 'Fila de chamados' : 'Meus chamados';
  }

  formatPriority(priority?: ControlHelpPriority | null) {
    const map: Record<string, string> = {
      LOW: 'Baixa',
      MEDIUM: 'Média',
      HIGH: 'Alta',
      URGENT: 'Urgente'
    };
    return priority ? (map[priority] || priority) : '—';
  }

  lastUpdateLabel(ticket: ControlHelpTicket) {
    const date = ticket.last_action_at || ticket.opened_at;
    if (!date) {
      return '—';
    }
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) {
      return '—';
    }
    return parsed.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  openedDaysLabel(ticket: ControlHelpTicket) {
    if (!ticket.opened_at) {
      return '—';
    }
    const opened = new Date(ticket.opened_at);
    if (Number.isNaN(opened.getTime())) {
      return '—';
    }
    const diffMs = Date.now() - opened.getTime();
    const days = Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
    if (days === 0) {
      return 'hoje';
    }
    if (days === 1) {
      return 'há 1 dia';
    }
    return `há ${days} dias`;
  }

  creatorLabel(ticket: ControlHelpTicket) {
    return ticket.created_by_name || this.userNameById(ticket.created_by);
  }

  formatDateTime(value?: string | null) {
    if (!value) {
      return '—';
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return '—';
    }
    return parsed.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  formatDate(value?: string | null) {
    if (!value) {
      return '—';
    }
    const trimmed = String(value).trim().slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      const [year, month, day] = trimmed.split('-');
      return `${day}/${month}/${year}`;
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return '—';
    }
    return parsed.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  formatDuration(start?: string | null, end?: string | null) {
    const startDate = this.toValidDate(start);
    const endDate = end ? this.toValidDate(end) : new Date();
    if (!startDate || !endDate) {
      return '—';
    }
    const diffMinutes = Math.max(0, Math.round((endDate.getTime() - startDate.getTime()) / 60000));
    const days = Math.floor(diffMinutes / 1440);
    const hours = Math.floor((diffMinutes % 1440) / 60);
    const minutes = diffMinutes % 60;
    const parts: string[] = [];
    if (days > 0) {
      parts.push(`${days}d`);
    }
    if (hours > 0) {
      parts.push(`${hours}h`);
    }
    if (minutes > 0 || !parts.length) {
      parts.push(`${minutes}m`);
    }
    return parts.join(' ');
  }

  firstInProgressLabel(ticket: ControlHelpTicket) {
    if (!ticket.first_in_progress_at) {
      return 'Ainda não iniciado';
    }
    return this.formatDateTime(ticket.first_in_progress_at);
  }

  resolvedAtLabel(ticket: ControlHelpTicket) {
    if (!ticket.resolved_at && !ticket.closed_at) {
      return 'Em aberto';
    }
    return this.formatDateTime(ticket.resolved_at || ticket.closed_at);
  }

  timeToStartLabel(ticket: ControlHelpTicket) {
    if (!ticket.first_in_progress_at) {
      return 'Ainda não iniciado';
    }
    return this.formatDuration(ticket.opened_at, ticket.first_in_progress_at);
  }

  timeToResolveLabel(ticket: ControlHelpTicket) {
    const endAt = ticket.resolved_at || ticket.closed_at || null;
    if (!endAt) {
      return `Em aberto há ${this.formatDuration(ticket.opened_at, null)}`;
    }
    return this.formatDuration(ticket.opened_at, endAt);
  }

  handlingTimeLabel(ticket: ControlHelpTicket) {
    if (!ticket.first_in_progress_at) {
      return 'Ainda não iniciado';
    }
    const endAt = ticket.resolved_at || ticket.closed_at || null;
    return this.formatDuration(ticket.first_in_progress_at, endAt);
  }

  slaResponseLabel(ticket: ControlHelpTicket) {
    const sla = this.slaPolicyForTicket(ticket);
    if (!sla) {
      return 'Sem SLA';
    }
    const opened = this.toValidDate(ticket.opened_at);
    if (!opened) {
      return 'Sem SLA';
    }
    const deadline = opened.getTime() + sla.response_time_minutes * 60 * 1000;
    const responseAt = this.toValidDate(ticket.response_at);
    if (responseAt) {
      return responseAt.getTime() <= deadline ? 'Dentro do SLA' : 'Fora do SLA';
    }
    return Date.now() <= deadline ? 'Pendente' : 'Fora do SLA';
  }

  slaResolutionLabel(ticket: ControlHelpTicket) {
    const sla = this.slaPolicyForTicket(ticket);
    if (!sla) {
      return 'Sem SLA';
    }
    const dueAt = this.toValidDate(ticket.due_at);
    if (!dueAt) {
      return 'Sem SLA';
    }
    const endAt = this.toValidDate(ticket.closed_at);
    if (endAt) {
      return endAt.getTime() <= dueAt.getTime() ? 'Dentro do SLA' : 'Fora do SLA';
    }
    return Date.now() <= dueAt.getTime() ? 'Pendente' : 'Fora do SLA';
  }

  slaBadgeClass(label: string) {
    if (label === 'Dentro do SLA') {
      return 'ok';
    }
    if (label === 'Fora do SLA') {
      return 'overdue';
    }
    if (label === 'Pendente') {
      return 'pending';
    }
    return 'na';
  }

  private slaPolicyForTicket(ticket: ControlHelpTicket) {
    if (!ticket.department_id || !ticket.priority) {
      return null;
    }
    return (
      this.slaPolicies.find(
        (sla) =>
          Number(sla.department_id) === Number(ticket.department_id) &&
          sla.priority === ticket.priority
      ) || null
    );
  }

  private toValidDate(value?: string | null) {
    if (!value) {
      return null;
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }
    return parsed;
  }

  userNameById(id?: number | null) {
    if (!id) {
      return 'Não atribuído';
    }
    return this.users.find((u) => u.id === id)?.name || 'Não atribuído';
  }

  fileUrl(file: ControlHelpAttachment) {
    if (!file.file_path) {
      return '#';
    }
    if (file.file_path.startsWith('http')) {
      return file.file_path;
    }
    return `${API_BASE_URL}${file.file_path}`;
  }

  commentAttachments(commentId: number) {
    return this.ticketAttachments.filter((file) => Number(file.comment_id) === Number(commentId));
  }

  standaloneAttachments() {
    return this.ticketAttachments.filter((file) => !file.comment_id);
  }

  historyLabel(item: ControlHelpHistory) {
    if (item.action_type === 'COMMENTED') {
      return 'Comment added';
    }
    if (item.description) {
      return item.description;
    }
    const map: Record<string, string> = {
      CREATED: 'Ticket created',
      COMMENTED: 'Comment added',
      STATUS_CHANGED: 'Status updated',
      PRIORITY_CHANGED: 'Priority updated',
      ASSIGNED: 'Assignee updated',
      ATTACHMENT_ADDED: 'Attachment added',
      WATCHERS_UPDATED: 'Watchers updated'
    };
    return map[item.action_type] || item.action_type;
  }

  historyDetail(item: ControlHelpHistory) {
    if (item.action_type === 'COMMENTED') {
      if (item.new_value) {
        return item.new_value;
      }
      if (item.description && item.description.startsWith('Comentário:')) {
        return item.description.replace(/^Comentário:\s*/i, '');
      }
      return '';
    }

    if (item.action_type === 'ASSIGNED') {
      const oldUser = this.userNameById(Number(item.old_value || 0));
      const newUser = this.userNameById(Number(item.new_value || 0));
      if (!item.old_value && item.new_value) {
        return `Novo valor: ${newUser}`;
      }
      if (item.old_value && !item.new_value) {
        return `Valor anterior: ${oldUser}`;
      }
      if (!oldUser && !newUser) {
        return '';
      }
      return `${oldUser} → ${newUser}`;
    }

    if (item.action_type === 'WATCHERS_UPDATED') {
      if (!item.new_value) {
        return 'Observadores removidos';
      }
      try {
        const parsed = JSON.parse(String(item.new_value));
        if (Array.isArray(parsed)) {
          const labels = parsed.map((value) => this.userNameById(Number(value))).filter((label) => label && label !== 'Não atribuído');
          if (labels.length) {
            return `Novo valor: ${labels.join(', ')}`;
          }
        }
      } catch (_) {}
      return `Novo valor: ${item.new_value}`;
    }

    const oldValueRaw = String(item.old_value || '').trim();
    const newValueRaw = String(item.new_value || '').trim();
    const oldValue = item.action_type === 'STATUS_CHANGED' ? this.statusLabel(oldValueRaw as ControlHelpStatus) : oldValueRaw;
    const newValue = item.action_type === 'STATUS_CHANGED' ? this.statusLabel(newValueRaw as ControlHelpStatus) : newValueRaw;
    if (!oldValue && !newValue) {
      return '';
    }
    if (!oldValue && newValue) {
      return `Novo valor: ${newValue}`;
    }
    if (oldValue && !newValue) {
      return `Valor anterior: ${oldValue}`;
    }
    return `${oldValue} → ${newValue}`;
  }

  statusLabel(status?: ControlHelpStatus | string | null) {
    const map: Record<string, string> = {
      OPEN: 'Aberto',
      IN_PROGRESS: 'Em andamento',
      WAITING_USER: 'Aguardando usuário',
      WAITING_THIRD_PARTY: 'Aguardando terceiro',
      RESOLVED: 'Resolvido',
      CLOSED: 'Fechado',
      CANCELED: 'Cancelado'
    };
    if (!status) {
      return '—';
    }
    return map[String(status).toUpperCase()] || String(status).replace(/_/g, ' ');
  }

  historyActionTag(item: ControlHelpHistory) {
    const map: Record<string, string> = {
      CREATED: 'CREATED',
      COMMENTED: 'COMMENT',
      STATUS_CHANGED: 'STATUS',
      PRIORITY_CHANGED: 'PRIORITY',
      ASSIGNED: 'ASSIGNEE',
      ATTACHMENT_ADDED: 'ATTACHMENT',
      WATCHERS_UPDATED: 'WATCHERS'
    };
    return map[item.action_type] || 'UPDATE';
  }

  historyTone(item: ControlHelpHistory) {
    const map: Record<string, string> = {
      CREATED: 'success',
      COMMENTED: 'info',
      STATUS_CHANGED: 'primary',
      PRIORITY_CHANGED: 'warning',
      ASSIGNED: 'primary',
      ATTACHMENT_ADDED: 'info',
      WATCHERS_UPDATED: 'primary'
    };
    return map[item.action_type] || 'neutral';
  }

  historyIcon(item: ControlHelpHistory) {
    const map: Record<string, string> = {
      CREATED: 'add_circle',
      COMMENTED: 'chat',
      STATUS_CHANGED: 'sync_alt',
      PRIORITY_CHANGED: 'flag',
      ASSIGNED: 'person_add',
      ATTACHMENT_ADDED: 'attach_file',
      WATCHERS_UPDATED: 'visibility'
    };
    return map[item.action_type] || 'history';
  }

  canEditActiveTicket() {
    if (!this.activeTicket) {
      return false;
    }
    if (Number(this.activeTicket.assigned_to) === Number(this.userId)) {
      return true;
    }
    const scopeMode = this.getControlhelpScopeMode();
    if (scopeMode === 'all') {
      return true;
    }
    const scopeDepartmentIds = this.getControlhelpScopeDepartmentIdsForFilters();
    if (scopeDepartmentIds.length) {
      return scopeDepartmentIds.includes(Number(this.activeTicket.department_id));
    }
    if (!this.userDepartmentId) {
      return false;
    }
    return Number(this.activeTicket.department_id) === Number(this.userDepartmentId);
  }

  canEditAssigneeAndPriority() {
    if (!this.activeTicket) {
      return false;
    }
    const scopeMode = this.getControlhelpScopeMode();
    if (scopeMode === 'all') {
      return true;
    }
    const isCurrentAssignee = Number(this.activeTicket.assigned_to) === Number(this.userId);
    const isSameDepartment = Number(this.activeTicket.department_id) === Number(this.userDepartmentId);
    if (isCurrentAssignee && !isSameDepartment) {
      return false;
    }
    return this.canEditActiveTicket();
  }

  canCreateInternalComment() {
    if (!this.activeTicket || !this.userId) {
      return false;
    }
    if (Number(this.activeTicket.assigned_to || 0) === Number(this.userId)) {
      return true;
    }
    const demandedDepartmentId = Number(this.activeTicket.department_id);
    if (!Number.isFinite(demandedDepartmentId) || demandedDepartmentId <= 0) {
      return false;
    }
    if (this.controlhelpDepartmentIds.includes(demandedDepartmentId)) {
      return true;
    }
    return Number(this.userDepartmentId || 0) === demandedDepartmentId;
  }

  formatDateInput(value?: string | null) {
    if (!value) {
      return '';
    }
    const trimmed = String(value).trim().slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return trimmed;
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return '';
    }
    return toLocalISODate(parsed);
  }

  watchersAvailableUsers() {
    const usedIds = new Set(this.ticketWatchers.map((watcher) => Number(watcher.user_id)));
    const term = this.normalizePeopleSearch(this.watcherSearch);
    return this.users.filter((user) =>
      !usedIds.has(Number(user.id))
      && this.isUserAssignableInControlhelp(user)
      && (!term || this.normalizePeopleSearch(user.name).includes(term))
    );
  }

  watcherLabel(watcher: ControlHelpWatcher) {
    return watcher.user_name || this.userNameById(watcher.user_id);
  }

  formatAttachmentName(fileName: string, limit = 56) {
    const fallback = 'arquivo';
    if (!fileName) {
      return fallback;
    }
    let normalized = String(fileName).trim();
    try {
      normalized = decodeURIComponent(normalized);
    } catch (_) {}
    try {
      normalized = decodeURIComponent(escape(normalized));
    } catch (_) {}
    normalized = normalized.replace(/\s+/g, ' ').trim();
    if (!normalized) {
      return fallback;
    }
    if (normalized.length <= limit) {
      return normalized;
    }
    const extIndex = normalized.lastIndexOf('.');
    const hasExt = extIndex > 0 && extIndex < normalized.length - 1;
    if (!hasExt) {
      return `${normalized.slice(0, Math.max(0, limit - 1))}…`;
    }
    const ext = normalized.slice(extIndex);
    const base = normalized.slice(0, extIndex);
    const maxBase = Math.max(1, limit - ext.length - 1);
    return `${base.slice(0, maxBase)}…${ext}`;
  }

  private ticketsForView() {
    if (!this.controlhelpSectorEnabled) {
      return this.tickets.filter((t) => this.isUserRelevantTicket(t));
    }
    const scopeMode = this.getControlhelpScopeMode();
    const scopeDepartmentIds = this.getControlhelpScopeDepartmentIdsForFilters();
    if (this.ticketView === 'queue') {
      if (scopeMode === 'all') {
        return this.tickets;
      }
      return this.tickets.filter((t) => {
        const inScopeDepartment = scopeDepartmentIds.length
          ? scopeDepartmentIds.includes(Number(t.department_id))
          : false;
        const assignedToMe = Number(t.assigned_to) === Number(this.userId);
        return inScopeDepartment || assignedToMe;
      });
    }
    if (scopeMode === 'all') {
      return this.tickets.filter((t) => this.isUserRelevantTicket(t));
    }
    return this.tickets.filter((t) => this.isUserRelevantTicket(t));
  }

  private ticketsForSummary() {
    if (!this.controlhelpSectorEnabled) {
      return this.tickets.filter((t) => this.isUserRelevantTicket(t));
    }
    const scopeMode = this.getControlhelpScopeMode();
    const scopeDepartmentIds = this.getControlhelpScopeDepartmentIdsForFilters();
    if (scopeMode === 'all') {
      return this.tickets.filter((t) => this.isUserRelevantTicket(t));
    }
    const queueTickets = scopeDepartmentIds.length
      ? this.tickets.filter((t) => scopeDepartmentIds.includes(Number(t.department_id)))
      : [];
    const mineTickets = this.tickets.filter((t) => this.isUserRelevantTicket(t) && (!scopeDepartmentIds.length || !scopeDepartmentIds.includes(Number(t.department_id))));
    const combined = [...queueTickets, ...mineTickets];
    const unique = new Map<number, ControlHelpTicket>();
    combined.forEach((ticket) => unique.set(ticket.id, ticket));
    return Array.from(unique.values());
  }

  private applyTicketFilters(
    tickets: ControlHelpTicket[],
    options: { hideClosedByDefault?: boolean } = {}
  ) {
    const term = (this.searchTerm || '').toLowerCase();
    const hideClosed =
      Boolean(options.hideClosedByDefault) && this.statusFilter === 'ALL';
    return tickets.filter((ticket) => {
      if (
        hideClosed &&
        (ticket.status === 'RESOLVED' || ticket.status === 'CLOSED' || ticket.status === 'CANCELED')
      ) {
        return false;
      }
      if (this.statusFilter !== 'ALL' && ticket.status !== this.statusFilter) {
        return false;
      }
      if (this.priorityFilter !== 'ALL' && ticket.priority !== this.priorityFilter) {
        return false;
      }
      if (this.departmentFilter !== 'ALL' && Number(ticket.department_id) !== Number(this.departmentFilter)) {
        return false;
      }
      if (!term) {
        return true;
      }
      return (
        ticket.title.toLowerCase().includes(term) ||
        ticket.ticket_code.toLowerCase().includes(term) ||
        (ticket.category || '').toLowerCase().includes(term)
      );
    });
  }

  private applyTicketSort(tickets: ControlHelpTicket[]) {
    const direction = this.sortOrder === 'newest' ? -1 : 1;
    return [...tickets].sort((a, b) => {
      const aDate = new Date(a.opened_at || 0).getTime();
      const bDate = new Date(b.opened_at || 0).getTime();
      const safeA = Number.isFinite(aDate) ? aDate : 0;
      const safeB = Number.isFinite(bDate) ? bDate : 0;
      if (safeA !== safeB) {
        return (safeA - safeB) * direction;
      }
      return (Number(a.id) - Number(b.id)) * direction;
    });
  }

  private isUserRelevantTicket(ticket: ControlHelpTicket) {
    if (!this.userId) {
      return false;
    }
    return Number(ticket.created_by) === Number(this.userId) || Number(ticket.assigned_to) === Number(this.userId);
  }

  private filteredSummaryTickets() {
    return this.applyTicketFilters(this.ticketsForSummary());
  }

  private fetchTickets() {
    if (!this.tenantId) {
      return;
    }
    this.controlhelpService
      .getTickets(this.tenantId, { status: this.statusFilter, priority: this.priorityFilter, search: this.searchTerm })
      .subscribe({
        next: (rows) => {
          this.tickets = rows || [];
          if (this.ticketPage > this.ticketTotalPages) {
            this.ticketPage = this.ticketTotalPages;
          }
          this.refreshDashboardCharts();
        },
        error: () => {
          this.tickets = [];
          this.refreshDashboardCharts();
        }
      });
  }

  private fetchComments(ticketId: number) {
    if (!this.tenantId) {
      return;
    }
    this.controlhelpService.getComments(this.tenantId, ticketId, this.userId).subscribe({
      next: (rows) => (this.ticketComments = rows || []),
      error: () => (this.ticketComments = [])
    });
  }

  private fetchAttachments(ticketId: number) {
    if (!this.tenantId) {
      return;
    }
    this.controlhelpService.getAttachments(this.tenantId, ticketId, this.userId).subscribe({
      next: (rows) => (this.ticketAttachments = rows || []),
      error: () => {
        this.ticketAttachments = [];
        this.setDetailFeedback('error', 'Não foi possível carregar os anexos.');
      }
    });
  }

  private fetchHistory(ticketId: number) {
    if (!this.tenantId) {
      return;
    }
    this.controlhelpService.getHistory(this.tenantId, ticketId, this.userId).subscribe({
      next: (rows) => (this.ticketHistory = rows || []),
      error: () => (this.ticketHistory = [])
    });
  }

  private fetchWatchers(ticketId: number) {
    if (!this.tenantId) {
      return;
    }
    this.controlhelpService.getWatchers(this.tenantId, ticketId).subscribe({
      next: (rows) => {
        this.ticketWatchers = (rows || []).map((watcher) => ({ ...watcher }));
        this.detailOriginalWatchers = this.ticketWatchers.map((watcher) => ({ ...watcher }));
      },
      error: () => {
        this.ticketWatchers = [];
        this.detailOriginalWatchers = [];
        this.setDetailFeedback('error', 'Não foi possível carregar observadores.');
      }
    });
  }

  private fetchCategories() {
    if (!this.tenantId) {
      return;
    }
    // Carrega todas as categorias para permitir seleção por departamento no formulário.
    this.controlhelpService.getCategories(this.tenantId).subscribe({
      next: (rows) => (this.categories = rows || []),
      error: () => (this.categories = [])
    });
  }

  private fetchSla() {
    if (!this.tenantId) {
      return;
    }
    this.controlhelpService.getSla(this.tenantId).subscribe({
      next: (rows) => (this.slaPolicies = rows || []),
      error: () => (this.slaPolicies = [])
    });
  }

  private fetchUsers() {
    if (!this.tenantId) {
      return;
    }
    this.userService.getUsersByTenant(this.tenantId, undefined, true).subscribe({
      next: (rows) => {
        this.users = (rows || []).slice().sort((a: any, b: any) => String(a?.name || '').localeCompare(String(b?.name || '')));
      },
      error: () => {
        this.users = [];
      }
    });
  }

  private fetchDepartments() {
    if (!this.tenantId) {
      return;
    }
    forkJoin({
      all: this.orgStructureService.getDepartments(this.tenantId),
      origin: this.orgStructureService.getDepartments(this.tenantId, null, true, 'ORIGIN'),
      destination: this.orgStructureService.getDepartments(this.tenantId, null, true, 'DESTINATION')
    }).subscribe({
      next: ({ all, origin, destination }) => {
        const sorter = (a: any, b: any) => {
          const labelA = a.sector_name ? `${a.sector_name} / ${a.name}` : a.name;
          const labelB = b.sector_name ? `${b.sector_name} / ${b.name}` : b.name;
          return String(labelA).localeCompare(String(labelB));
        };
        this.allDepartments = (all || []).slice().sort(sorter);
        this.originDepartmentsList = (origin || []).slice().sort(sorter);
        this.departments = (destination || []).slice().sort(sorter);
        if (!this.userSectorId && this.userDepartmentId) {
          const dept =
            this.allDepartments.find((d) => Number(d.id) === Number(this.userDepartmentId)) ||
            this.departments.find((d) => Number(d.id) === Number(this.userDepartmentId));
          this.userSectorId = (dept?.sector_id ?? null) as number | null;
        }
        const defaultDepartmentId = this.getDefaultControlhelpOriginDepartmentId();
        if (defaultDepartmentId) {
          this.newTicketForm.origin_department_id = String(defaultDepartmentId);
        }
      },
      error: () => {
        this.departments = [];
        this.allDepartments = [];
        this.originDepartmentsList = [];
      }
    });
  }

  private resetNewTicketForm() {
    const defaultDepartmentId = this.getDefaultControlhelpOriginDepartmentId();
    this.newTicketForm = {
      title: '',
      description: '',
      priority: 'MEDIUM',
      category: '',
      subcategory: '',
      department_id: '',
      origin_department_id: defaultDepartmentId ? String(defaultDepartmentId) : ''
    };
    this.newTicketStep = 1;
  }

  private getDefaultControlhelpOriginDepartmentId() {
    const scopeDepartmentIds = this.getControlhelpScopeDepartmentIdsForFilters();
    if (scopeDepartmentIds.length) {
      return scopeDepartmentIds[0];
    }
    if (this.userDepartmentId) {
      return Number(this.userDepartmentId);
    }
    return null;
  }

  goToTicketStep(step: number) {
    if (step < 1 || step > 3) {
      return;
    }
    this.newTicketStep = step;
  }

  nextTicketStep() {
    if (this.newTicketStep < 3) {
      this.newTicketStep += 1;
    }
  }

  prevTicketStep() {
    if (this.newTicketStep > 1) {
      this.newTicketStep -= 1;
    }
  }

  onDepartmentChange() {
    this.newTicketForm.category = '';
    this.newTicketForm.subcategory = '';
  }

  categoriesByDepartment() {
    const id = Number(this.newTicketForm.department_id);
    if (!id) {
      return [];
    }
    const unique = new Map<string, ControlHelpCategory>();
    this.categories
      .filter((c) => Number(c.department_id) === id)
      .forEach((c) => {
        if (!unique.has(c.name)) {
          unique.set(c.name, c);
        }
      });
    return Array.from(unique.values()).sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }

  subcategoriesByDepartment() {
    const id = Number(this.newTicketForm.department_id);
    if (!id || !this.newTicketForm.category) {
      return [];
    }
    return this.categories
      .filter((c) => Number(c.department_id) === id && c.name === this.newTicketForm.category && c.subcategory)
      .map((c) => c.subcategory as string)
      .sort((a, b) => String(a).localeCompare(String(b)));
  }

  originDepartments() {
    const source = this.originDepartmentsList.length ? this.originDepartmentsList : this.departments;
    const scopeDepartmentIds = this.getControlhelpScopeDepartmentIdsForFilters();
    if (scopeDepartmentIds.length) {
      return source.filter((dept) => scopeDepartmentIds.includes(Number(dept.id)));
    }
    if (this.userSectorId) {
      return source.filter((dept) => Number(dept.sector_id) === Number(this.userSectorId));
    }
    if (this.userDepartmentId) {
      return source.filter((dept) => Number(dept.id) === Number(this.userDepartmentId));
    }
    return source;
  }

  departmentLabel(departmentId?: number | null) {
    const dept =
      this.allDepartments.find((d) => d.id === Number(departmentId)) ||
      this.departments.find((d) => d.id === Number(departmentId));
    if (!dept) {
      return 'Não informado';
    }
    return dept.sector_name ? `${dept.sector_name} / ${dept.name}` : dept.name;
  }

  usersForAssignedTicket() {
    return this.users.filter((user) => this.isUserAssignableInControlhelp(user));
  }

  private resolveUserControlhelpSectorId(user: any) {
    const directSectorId = Number(user?.sector_id);
    if (Number.isFinite(directSectorId) && directSectorId > 0) {
      return directSectorId;
    }
    const departmentId = Number(user?.department_id);
    if (!Number.isFinite(departmentId) || departmentId <= 0) {
      return null;
    }
    const dept =
      this.allDepartments.find((item) => Number(item.id) === departmentId) ||
      this.departments.find((item) => Number(item.id) === departmentId);
    const sectorId = Number(dept?.sector_id);
    return Number.isFinite(sectorId) && sectorId > 0 ? sectorId : null;
  }

  private isUserAssignableInControlhelp(user: any) {
    if (!user?.id) {
      return false;
    }
    const roleName = String(user?.role_name ?? user?.role?.role_name ?? user?.role?.name ?? '')
      .trim()
      .toLowerCase();
    const roleId = Number(user?.role_id);
    const isMasterOrAdmin = Boolean(user?.is_master ?? user?.isMaster ?? user?.master)
      || roleId === 1
      || roleName.includes('master')
      || roleName.includes('admin');
    if (isMasterOrAdmin) {
      return true;
    }
    const sectorId = this.resolveUserControlhelpSectorId(user);
    if (!sectorId) {
      return false;
    }
    if (this.controlhelpDestinationSectorIds === null) {
      return true;
    }
    return this.controlhelpDestinationSectorIds.includes(Number(sectorId));
  }

  departmentLabelFromForm(value: string | number | null | undefined) {
    if (value === null || typeof value === 'undefined' || value === '') {
      return 'Não informado';
    }
    return this.departmentLabel(Number(value));
  }

  departmentsForCadastros() {
    if (this.isMasterUser) {
      return this.departments;
    }
    if (!this.userDepartmentId) {
      return [];
    }
    return this.departments.filter((dept) => Number(dept.id) === Number(this.userDepartmentId));
  }

  private uploadNewTicketAttachments(ticketId: number) {
    if (!this.tenantId || !this.userId || !this.newTicketFiles.length) {
      return of(true);
    }
    const uploads = this.newTicketFiles.map((file) => {
      const formData = new FormData();
      formData.append('tenant_id', String(this.tenantId));
      formData.append('uploaded_by', String(this.userId));
      formData.append('file', file);
      return this.controlhelpService.uploadAttachment(ticketId, formData, this.tenantId ?? undefined).pipe(
        map(() => true),
        catchError(() => of(false))
      );
    });
    return forkJoin(uploads.length ? uploads : [of(true)]).pipe(
      map((results) => results.every(Boolean))
    );
  }

  private setTicketFormFeedback(type: 'error' | 'success' | 'info', message: string) {
    this.ticketFormFeedbackType = type;
    this.ticketFormFeedback = message;
  }

  private newTicketFileKey(file: File) {
    return `${file.name}_${file.size}_${file.lastModified}`;
  }

  private revokeNewTicketFilePreview(file: File) {
    const key = this.newTicketFileKey(file);
    const url = this.newTicketFilePreviewUrls.get(key);
    if (!url) {
      return;
    }
    URL.revokeObjectURL(url);
    this.newTicketFilePreviewUrls.delete(key);
  }

  private clearNewTicketFilePreviews() {
    this.newTicketFiles.forEach((file) => this.revokeNewTicketFilePreview(file));
  }

  private setDetailFeedback(type: 'error' | 'success' | 'info', message: string) {
    this.detailFeedbackType = type;
    this.detailFeedback = message;
  }

  private setPageFeedback(type: 'error' | 'success' | 'info', message: string) {
    this.pageFeedback = { type, message };
  }

  dismissTicketCreateFeedback(closeModal = false) {
    const wasSuccess = this.ticketCreateFeedbackState === 'success';
    this.ticketCreateFeedbackState = 'idle';
    this.ticketCreateFeedbackMessage = '';
    this.ticketCreateFeedbackDetail = '';
    if (closeModal && wasSuccess) {
      this.closeNewTicket();
    }
  }

  private toDateValue(value?: string | null) {
    const normalized = this.formatDateInput(value);
    if (!normalized) {
      return null;
    }
    const [year, month, day] = normalized.split('-').map((item) => Number(item));
    if (!year || !month || !day) {
      return null;
    }
    const parsed = new Date(year, month - 1, day);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }
    return parsed;
  }

  private resolveControlhelpManagePermission(user: any) {
    if (this.isMasterUser || this.isAdminUser) {
      return true;
    }
    const modules = Array.isArray(user?.modules) ? user.modules : [];
    const controlhelpModule = modules.find((module: any) => String(module?.module_key || '').toLowerCase() === 'controlhelp');
    return Boolean(controlhelpModule?.can_manage);
  }

}
